(() => {
  "use strict";

  const LOGIN_URL =
    "https://api.goprovalo.com/auth/shopify_customer_login.php";

  const RETURN_PATH = "/pages/subscription-plans";

  const PLAN_NAMES = {
    explorer: "Explorer",
    investor: "Investor",
    professional: "Professional"
  };

  const state = {
    authenticated: false,
    loadingCustomer: true,
    currentPlan: null,
    checkoutInProgress: false
  };

  document.addEventListener("DOMContentLoaded", initializePricingPage);

  async function initializePricingPage() {
    const pricingSection = getPricingSection();

    if (!pricingSection) {
      return;
    }

    preparePlanButtons(pricingSection);
    await loadCurrentMembership(pricingSection);
  }

  function getPricingSection() {
    return document.querySelector("[data-gp-pricing]");
  }

  function preparePlanButtons(pricingSection) {
    const paidButtons = pricingSection.querySelectorAll(
      'button[data-plan-action="Investor"], ' +
      'button[data-plan-action="Professional"]'
    );

    paidButtons.forEach((button) => {
      button.addEventListener("click", handlePaidPlanSelection);
    });
  }

  async function loadCurrentMembership(pricingSection) {
    const dashboardApi = pricingSection.dataset.dashboardApi;

    if (!dashboardApi) {
      state.loadingCustomer = false;

      showMessage(
        pricingSection,
        "Membership information is temporarily unavailable.",
        "error"
      );

      return;
    }

    try {
      const response = await fetch(dashboardApi, {
        method: "POST",
        credentials: "include",
        mode: "cors",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({})
      });

      const data = await parseJsonResponse(response);

      if (
        response.status === 401 ||
        data.error === "customer_not_authenticated"
      ) {
        state.authenticated = false;
        state.loadingCustomer = false;
        state.currentPlan = null;

        updateGuestButtons(pricingSection);
        return;
      }

      if (!response.ok || data.success !== true) {
        throw new Error(
          data.message ||
          data.error ||
          "Unable to load membership information."
        );
      }

      state.authenticated = true;
      state.loadingCustomer = false;
      state.currentPlan = normalizePlanName(
        data.subscription?.plan
      );

      updateCurrentPlanDisplay(pricingSection);
    } catch (error) {
      console.error(
        "GoProvalo membership loading error:",
        error
      );

      state.authenticated = false;
      state.loadingCustomer = false;
      state.currentPlan = null;

      updateGuestButtons(pricingSection);

      showMessage(
        pricingSection,
        "We could not verify your current membership. " +
        "You can sign in and try again.",
        "error"
      );
    }
  }

  async function handlePaidPlanSelection(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const pricingSection = getPricingSection();

    if (!pricingSection || !(button instanceof HTMLButtonElement)) {
      return;
    }

    if (button.disabled || state.checkoutInProgress) {
      return;
    }

    if (!isCheckoutEnabled(pricingSection)) {
      showMessage(
        pricingSection,
        "Membership checkout has not been enabled yet.",
        "notice"
      );

      return;
    }

    if (state.loadingCustomer) {
      showMessage(
        pricingSection,
        "Please wait while we verify your membership.",
        "notice"
      );

      return;
    }

    if (!state.authenticated) {
      redirectToCustomerLogin();
      return;
    }

    const selectedPlan = normalizePlanName(
      button.dataset.planAction
    );

    if (!selectedPlan || selectedPlan === PLAN_NAMES.explorer) {
      showMessage(
        pricingSection,
        "The selected membership is not valid.",
        "error"
      );

      return;
    }

    if (state.currentPlan === selectedPlan) {
      showMessage(
        pricingSection,
        `You already have the ${selectedPlan} membership.`,
        "notice"
      );

      return;
    }

    /*
     * Until a paid-to-paid subscription replacement workflow is added,
     * prevent a customer from creating a second Appstle subscription.
     */
    if (isPaidPlan(state.currentPlan)) {
      showMessage(
        pricingSection,
        "You already have a paid membership. Use your membership " +
        "management portal before changing to another paid plan.",
        "notice"
      );

      return;
    }

    const variantId = normalizeNumericId(
      button.dataset.variantId
    );

    const sellingPlanId = normalizeNumericId(
      button.dataset.sellingPlanId
    );

    if (!variantId || !sellingPlanId) {
      showMessage(
        pricingSection,
        "This membership checkout is not configured correctly.",
        "error"
      );

      return;
    }

    await startSubscriptionCheckout({
      pricingSection,
      button,
      selectedPlan,
      variantId,
      sellingPlanId
    });
  }

  async function startSubscriptionCheckout({
    pricingSection,
    button,
    selectedPlan,
    variantId,
    sellingPlanId
  }) {
    state.checkoutInProgress = true;

    const originalText = button.textContent.trim();

    setAllPaidButtonsDisabled(pricingSection, true);

    button.classList.add("is-loading");
    button.setAttribute("aria-busy", "true");
    button.textContent = "Preparing secure checkout…";

    showMessage(
      pricingSection,
      `Preparing the ${selectedPlan} test checkout…`,
      "notice"
    );

    try {
      /*
       * Membership checkout must contain exactly one mapped
       * subscription item. Clearing the cart prevents unrelated
       * products or another membership from entering this checkout.
       */
      await clearShopifyCart();

      const cart = await addSubscriptionToCart({
        selectedPlan,
        variantId,
        sellingPlanId
      });

      verifySubscriptionCartLine({
        cart,
        variantId,
        sellingPlanId
      });

      showMessage(
        pricingSection,
        "Subscription added. Redirecting to secure Shopify checkout…",
        "success"
      );

      window.location.assign(
        `${getShopifyRoot()}checkout`
      );
    } catch (error) {
      console.error(
        "GoProvalo subscription checkout error:",
        error
      );

      /*
       * Remove any incomplete or incorrectly configured cart line.
       * Failure to clear here should not hide the original error.
       */
      try {
        await clearShopifyCart();
      } catch (clearError) {
        console.error(
          "Unable to clear incomplete membership cart:",
          clearError
        );
      }

      showMessage(
        pricingSection,
        error.message ||
        "Unable to prepare checkout. Please try again.",
        "error"
      );

      button.textContent = originalText;
      button.classList.remove("is-loading");
      button.removeAttribute("aria-busy");

      restorePaidButtonStates(pricingSection);
      state.checkoutInProgress = false;
    }
  }

  async function clearShopifyCart() {
    const response = await fetch(
      `${getShopifyRoot()}cart/clear.js`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        "Unable to prepare the Shopify cart."
      );
    }

    return parseJsonResponse(response);
  }

  async function addSubscriptionToCart({
    selectedPlan,
    variantId,
    sellingPlanId
  }) {
    const response = await fetch(
      `${getShopifyRoot()}cart/add.js`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              selling_plan: Number(sellingPlanId),
              properties: {
                "_GoProvalo membership": selectedPlan,
                "_GoProvalo billing period": "Monthly"
              }
            }
          ]
        })
      }
    );

    const data = await parseJsonResponse(response);

    if (!response.ok) {
      const message =
        data.description ||
        data.message ||
        "Shopify could not add this membership to checkout.";

      throw new Error(message);
    }

    return data;
  }

  function verifySubscriptionCartLine({
    cart,
    variantId,
    sellingPlanId
  }) {
    const line = Array.isArray(cart.items)
      ? cart.items[0]
      : cart;

    if (!line) {
      throw new Error(
        "Shopify did not return the membership cart item."
      );
    }

    const returnedVariantId = normalizeNumericId(
      line.variant_id ?? line.id
    );

    const returnedSellingPlanId = normalizeNumericId(
      line.selling_plan_allocation?.selling_plan?.id
    );

    if (returnedVariantId !== variantId) {
      throw new Error(
        "Shopify returned an unexpected membership variant."
      );
    }

    /*
     * This check is critical. Without a selling-plan allocation,
     * Shopify would be treating the item as a one-time purchase.
     */
    if (!returnedSellingPlanId) {
      throw new Error(
        "The subscription selling plan was not applied. " +
        "Checkout was stopped to prevent a one-time purchase."
      );
    }

    if (returnedSellingPlanId !== sellingPlanId) {
      throw new Error(
        "Shopify returned an unexpected subscription selling plan."
      );
    }

    if (Number(line.quantity) !== 1) {
      throw new Error(
        "The membership cart quantity is invalid."
      );
    }
  }

  function updateCurrentPlanDisplay(pricingSection) {
    const cards = pricingSection.querySelectorAll(
      "[data-plan]"
    );

    cards.forEach((card) => {
      const cardPlan = normalizePlanName(
        card.dataset.plan
      );

      const action = card.querySelector(
        "[data-plan-action]"
      );

      card.classList.remove("current-plan");

      if (!action) {
        return;
      }

      if (cardPlan === state.currentPlan) {
        card.classList.add("current-plan");

        action.textContent = "Current Plan";
        action.setAttribute("aria-current", "true");

        if (action instanceof HTMLButtonElement) {
          action.disabled = true;
          action.setAttribute("aria-disabled", "true");
        }

        return;
      }

      action.removeAttribute("aria-current");
    });

    restorePaidButtonStates(pricingSection);
  }

  function updateGuestButtons(pricingSection) {
    if (!isCheckoutEnabled(pricingSection)) {
      return;
    }

    const paidButtons = pricingSection.querySelectorAll(
      'button[data-plan-action="Investor"], ' +
      'button[data-plan-action="Professional"]'
    );

    paidButtons.forEach((button) => {
      button.disabled = false;
      button.removeAttribute("aria-disabled");
      button.textContent =
        `Sign in to choose ${button.dataset.planAction}`;
    });
  }

  function restorePaidButtonStates(pricingSection) {
    const checkoutEnabled =
      isCheckoutEnabled(pricingSection);

    const paidButtons = pricingSection.querySelectorAll(
      'button[data-plan-action="Investor"], ' +
      'button[data-plan-action="Professional"]'
    );

    paidButtons.forEach((button) => {
      const buttonPlan = normalizePlanName(
        button.dataset.planAction
      );

      button.classList.remove("is-loading");
      button.removeAttribute("aria-busy");

      if (!checkoutEnabled) {
        button.disabled = true;
        button.setAttribute("aria-disabled", "true");
        button.textContent = "Checkout Coming Soon";
        return;
      }

      if (!state.authenticated) {
        button.disabled = false;
        button.removeAttribute("aria-disabled");
        button.textContent =
          `Sign in to choose ${buttonPlan}`;
        return;
      }

      if (buttonPlan === state.currentPlan) {
        button.disabled = true;
        button.setAttribute("aria-disabled", "true");
        button.textContent = "Current Plan";
        return;
      }

      if (isPaidPlan(state.currentPlan)) {
        button.disabled = true;
        button.setAttribute("aria-disabled", "true");
        button.textContent = "Manage Current Membership";
        return;
      }

      button.disabled = false;
      button.removeAttribute("aria-disabled");
      button.textContent =
        buttonPlan === PLAN_NAMES.investor
          ? "Test Investor Checkout"
          : "Test Professional Checkout";
    });
  }

  function setAllPaidButtonsDisabled(
    pricingSection,
    disabled
  ) {
    const paidButtons = pricingSection.querySelectorAll(
      'button[data-plan-action="Investor"], ' +
      'button[data-plan-action="Professional"]'
    );

    paidButtons.forEach((button) => {
      button.disabled = disabled;

      if (disabled) {
        button.setAttribute("aria-disabled", "true");
      } else {
        button.removeAttribute("aria-disabled");
      }
    });
  }

  function showMessage(
    pricingSection,
    message,
    type = "notice"
  ) {
    const messageElement = pricingSection.querySelector(
      "[data-pricing-message]"
    );

    if (!messageElement) {
      return;
    }

    messageElement.hidden = false;
    messageElement.textContent = message;
    messageElement.dataset.messageType = type;

    messageElement.classList.remove(
      "is-notice",
      "is-success",
      "is-error"
    );

    messageElement.classList.add(`is-${type}`);
  }

  function redirectToCustomerLogin() {
    const returnPath = encodeURIComponent(RETURN_PATH);

    window.location.assign(
      `${LOGIN_URL}?return=${returnPath}`
    );
  }

  function isCheckoutEnabled(pricingSection) {
    return pricingSection.dataset.checkoutEnabled === "true";
  }

  function isPaidPlan(planName) {
    return (
      planName === PLAN_NAMES.investor ||
      planName === PLAN_NAMES.professional
    );
  }

  function normalizePlanName(value) {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();

    if (
      normalized === "explorer" ||
      normalized === "free"
    ) {
      return PLAN_NAMES.explorer;
    }

    if (normalized === "investor") {
      return PLAN_NAMES.investor;
    }

    if (normalized === "professional") {
      return PLAN_NAMES.professional;
    }

    return null;
  }

  function normalizeNumericId(value) {
    const normalized = String(value ?? "").trim();

    if (!/^\d+$/.test(normalized)) {
      return null;
    }

    return normalized.replace(/^0+(?=\d)/, "");
  }

  function getShopifyRoot() {
    const root =
      window.Shopify?.routes?.root || "/";

    return root.endsWith("/")
      ? root
      : `${root}/`;
  }

  async function parseJsonResponse(response) {
    const responseText = await response.text();

    if (!responseText) {
      return {};
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      console.error(
        "Invalid JSON response:",
        responseText
      );

      throw new Error(
        "A service returned an invalid response."
      );
    }
  }
})();